import { Course, Item } from "../../types/learnPageSchema";
import cls from "./LearnPageSidebar.module.scss";
import React, { FC, useEffect, useState } from "react";
import { classNames } from "../../../../shared/lib/className/className";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
  MenuItem,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import arrow from "../../../../assets/icons/arrow.svg";
import DownloadIcon from "@mui/icons-material/Download";
import Menu from "@mui/material/Menu";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";

interface LearnDashboardSidebarProps {
  className?: string;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  studentCourse: Course;
  onItemSelect: (
    contentType: "article" | "video" | "slide" | "test" | "task",
    contentData: Item
  ) => void;
  passedPart: Item[];
  activeSection: any | null;
  activeLesson: any | null;
  studentId?: string;
}

interface DropdownButtonProps {
  fileName: any;
  link: any;
}

function DropdownButton({ fileName, link }: DropdownButtonProps) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box>
      <Button
        aria-controls="simple-menu"
        aria-haspopup="true"
        onClick={handleClick}
        startIcon={
          <FolderOpenIcon
            sx={{
              color: "#0573E4",
            }}
          />
        }>
        Ресурсы
      </Button>
      <Menu
        sx={{
          filter: "drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.05))",
        }}
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}>
        <MenuItem onClick={handleClose}>
          <a
            href={fileName}
            download={link}
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "flex",
              alignItems: "center",
            }}>
            <DownloadIcon />
            <Typography
              sx={{ fontWeight: 700, fontSize: "14px", marginLeft: 1 }}>
              {fileName}
            </Typography>
          </a>
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default DropdownButton;

export const LearnPageSidebar: FC<LearnDashboardSidebarProps> = ({
  className,
  studentCourse,
  onItemSelect,
  setCollapsed,
  collapsed,
  passedPart,
  activeSection,
  activeLesson,
  studentId,
}) => {
  const isMobile = useMediaQuery("(max-width:1000px)");
  const [expanded, setExpanded] = useState<string | false>("");
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const getContentType = (
    item: Item
  ): "article" | "video" | "slide" | "test" | "task" => {
    if (item.video && item.video_name) return "video";
    if (item.slides && item.slides_file_name) return "slide";
    if (item.test_name) return "test";
    if (item.exercise_name) return "task";
    return "article";
  };
  useEffect(() => {
    // @ts-ignore
    const lastOpenedContent = JSON.parse(
      // @ts-ignore
      localStorage.getItem(`lastOpenedContent-${studentId}`)
    );
    if (lastOpenedContent) {
      const { contentData } = lastOpenedContent;
      const contentType = getContentType(contentData);

      const foundPart = studentCourse.parts.find((part) =>
        part.items.some((item) => item.id === contentData.id)
      );

      if (foundPart) {
        const foundItem = foundPart.items.find(
          (item) => item.id === contentData.id
        );
        if (foundItem) {
          const partIndex = studentCourse.parts.indexOf(foundPart);
          setExpanded(`panel${partIndex}`);
          setSelectedItem(contentData.id);
          onItemSelect(contentType, foundItem);
        }
      }
    }
  }, [studentCourse, onItemSelect, studentId]);
  useEffect(() => {
    if (activeSection !== null) {
      setExpanded(`panel${activeSection}`);
    }
    if (activeLesson !== null) {
      setSelectedItem(activeLesson);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, activeLesson]);

  const handleSelectItem = (item: Item) => {
    setSelectedItem(item.id);
    onItemSelect(getContentType(item), item);
  };

  const onToggle = () => {
    setCollapsed(!collapsed);
  };

  return (
    <div
      className={classNames(
        cls.sidebar,
        { [cls.collapsed]: collapsed },
        className ? [className] : []
      )}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
        {collapsed ? (
          ""
        ) : (
          <Typography sx={{ fontSize: "20px", fontWeight: 800 }}>
            Материалы курса
          </Typography>
        )}

        {!isMobile && (
          <Button
            onClick={onToggle}
            sx={{
              width: collapsed ? "100%" : "auto",
              borderRadius: "8px",
              backgroundColor: null,
              color: "gray",
              textTransform: "none",
              fontSize: "16px",
              fontWeight: 700,
              "&:hover": {
                borderColor: "gray",
              },
              "& .MuiButton-startIcon": {
                rotate: collapsed ? "180deg" : "0deg",
              },
            }}
            startIcon={<img src={arrow} alt="arrow" />}>
            {collapsed ? "" : "Свернуть"}
          </Button>
        )}
      </Box>
      {!collapsed &&
        studentCourse?.parts.map((part, index) => (
          <Accordion
            key={part.id}
            expanded={expanded === `panel${index}`}
            onChange={handleAccordionChange(`panel${index}`)}
            sx={{
              boxShadow: "none",
              borderRadius: "8px",
              "&:not(:last-child)": {
                margin: "12px 0 12px 0",
              },
              "&:before": {
                display: "none",
              },
              "&.Mui-expanded": {
                borderRadius: "8px",
              },
              ...(expanded === `panel${index}` && {
                border: "2px solid #0573E4",
              }),
              "& .MuiAccordionSummary-root": {
                borderBottom:
                  expanded === `panel${index}`
                    ? "1px solid rgba(0, 0, 0, .125)"
                    : "none",
                borderRadius: "8px",
              },
              "& .MuiAccordionDetails-root": {
                borderTop: "none",
              },
            }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
              sx={{
                "& .MuiAccordionSummary-content": {
                  display: "flex",
                  flexDirection: "column",
                },
              }}>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 800,
                }}>{`Раздел ${index + 1}: ${part.name}`}</Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "gray",
                }}>{`0 | ${part.items.length} | 13 мин`}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List
                sx={{
                  padding: 0,
                  "& .MuiButtonBase-root": {
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                  },
                }}>
                {part.items.map((item, itemIndex: number) => {
                  if (
                    item.slides &&
                    item.slides_file_name &&
                    item.slides_file_extension
                  ) {
                    const downloadUrl = item.slides;
                    let fileName = item.slides_file_name;
                    fileName = fileName
                      .replace(/[|&;$%@"<>()+,]/g, "")
                      .replace(/\s+/g, "_");
                    fileName += "." + item.slides_file_extension;
                    return (
                      <ListItem
                        sx={{
                          padding: "5px 0",
                          "&:not(:first-of-type)": {
                            borderTop: "1px solid #e0e0e0",
                          },
                        }}
                        key={itemIndex}
                        button
                        onClick={() => {
                          let contentType:
                            | "article"
                            | "video"
                            | "slide"
                            | "test"
                            | "task";
                          if (
                            item.video &&
                            item.video_name &&
                            item.video_extension
                          ) {
                            contentType = "video";
                          } else if (
                            item.slides &&
                            item.slides_file_extension &&
                            item.slides_file_name
                          ) {
                            contentType = "slide";
                          } else if (item.test_name && item.questions) {
                            contentType = "test";
                          } else if (item.exercise_name && item.instructions) {
                            contentType = "task";
                          } else {
                            contentType = "article";
                          }
                          onItemSelect(contentType, item);
                          handleSelectItem(item);
                        }}>
                        <Checkbox
                          sx={{
                            padding: "0 8px 0 0",
                            "&.Mui-checked": {
                              color: selectedItem === item.id ? "#0573E4" : "",
                            },
                            "&:hover": {
                              backgroundColor: "transparent",
                            },
                          }}
                          checked={
                            item.completed ||
                            !!passedPart?.find((el) => el.id === item.id)
                          }
                          className={
                            selectedItem === item.id
                              ? cls.selectedItemCheckbox
                              : ""
                          }
                        />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                            }}
                            variant="body2">
                            {item.lecture_name}
                          </Typography>
                          <Box>
                            <a
                              href={downloadUrl}
                              download={fileName}
                              key={itemIndex}
                              className={cls.buttonDownload}>
                              <DownloadIcon className={cls.downloadIcon} />
                              <Typography
                                sx={{ fontWeight: 700, fontSize: "14px" }}>
                                Слайды
                              </Typography>
                            </a>
                          </Box>
                        </Box>
                      </ListItem>
                    );
                  } else if (item.file) {
                    return (
                      <ListItem
                        sx={{
                          padding: "5px 0",
                          "&:not(:first-of-type)": {
                            borderTop: "1px solid #e0e0e0",
                          },
                        }}
                        key={itemIndex}
                        button
                        onClick={() => {
                          let contentType:
                            | "article"
                            | "video"
                            | "slide"
                            | "test"
                            | "task";
                          if (
                            item.video &&
                            item.video_name &&
                            item.video_extension
                          ) {
                            contentType = "video";
                          } else if (
                            item.slides &&
                            item.slides_file_extension &&
                            item.slides_file_name
                          ) {
                            contentType = "slide";
                          } else if (item.test_name && item.questions) {
                            contentType = "test";
                          } else if (item.exercise_name && item.instructions) {
                            contentType = "task";
                          } else {
                            contentType = "article";
                          }
                          onItemSelect(contentType, item);
                          handleSelectItem(item);
                        }}>
                        <Checkbox
                          sx={{
                            padding: "0 8px 0 0",
                            "&.Mui-checked": {
                              color: selectedItem === item.id ? "#0573E4" : "",
                            },
                            "&:hover": {
                              backgroundColor: "transparent",
                            },
                          }}
                          checked={
                            item.completed ||
                            !!passedPart?.find((el) => el.id === item.id)
                          }
                          className={
                            selectedItem === item.id
                              ? cls.selectedItemCheckbox
                              : ""
                          }
                        />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                          }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                            }}
                            variant="body2">
                            {item.exercise_name}
                          </Typography>
                          <Box>
                            <DropdownButton
                              link={item.file}
                              fileName={item.file_name}
                            />
                          </Box>
                        </Box>
                      </ListItem>
                    );
                  } else {
                    return (
                      <ListItem
                        sx={{
                          padding: "5px 0",
                          "&:not(:first-of-type)": {
                            borderTop: "1px solid #e0e0e0",
                          },
                        }}
                        key={itemIndex}
                        button
                        onClick={() => {
                          let contentType:
                            | "article"
                            | "video"
                            | "slide"
                            | "test"
                            | "task";
                          if (
                            item.video &&
                            item.video_name &&
                            item.video_extension
                          ) {
                            contentType = "video";
                          } else if (
                            item.slides &&
                            item.slides_file_extension &&
                            item.slides_file_name
                          ) {
                            contentType = "slide";
                          } else if (item.test_name && item.questions) {
                            contentType = "test";
                          } else if (item.exercise_name && item.instructions) {
                            contentType = "task";
                          } else {
                            contentType = "article";
                          }
                          onItemSelect(contentType, item);
                          handleSelectItem(item);
                        }}>
                        <Checkbox
                          sx={{
                            padding: "0 8px 0 0",
                            "&.Mui-checked": {
                              color: selectedItem === item.id ? "#0573E4" : "",
                            },
                            "&:hover": {
                              backgroundColor: "transparent",
                            },
                          }}
                          checked={
                            item.completed ||
                            !!passedPart?.find((el) => el.id === item.id)
                          }
                          className={
                            selectedItem === item.id
                              ? cls.selectedItemCheckbox
                              : ""
                          }
                        />
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                            }}
                            variant="body2">
                            {item.lecture_name ||
                              item.test_name ||
                              item.task_name ||
                              item.exercise_name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "gray",
                            }}>{`0 | ${part.items.length} | 13 мин`}</Typography>
                        </Box>
                      </ListItem>
                    );
                  }
                })}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
    </div>
  );
};
